import { Component, inject, Input, OnChanges, OnDestroy, SimpleChange } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    RevisionLiability,
    TreatmentDesignRevisionJudgementForm,
    TreatmentDesignRevisionLiability,
    TreatmentDesignRevisionReason,
} from '../../core/model';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TreatmentTypeOriginalId } from '../../core/model/treatmentType';
import { TreatmentDesignRevisionClient } from '../../core/client/treatment-design-revision-client';
import { RevisionLiabilityStore } from '../../core/store';
import { distinctUntilChanged, firstValueFrom, Subscription } from 'rxjs';
import { RxHelper } from '../../core/utils/rxjxHelper';
import { DropdownModule } from 'primeng/dropdown';
import { ReactiveFormsModule } from '@angular/forms';
import { InputTextareaModule } from 'primeng/inputtextarea';

@Component({
    selector: 'app-design-revision-judgement-edit',
    standalone: true,
    imports: [CommonModule, RadioButtonModule, DropdownModule, ReactiveFormsModule, InputTextareaModule],
    templateUrl: './design-revision-judgement-edit.component.html',
    styleUrls: ['./design-revision-judgement-edit.component.scss'],
    providers: [RxHelper],
})
export class DesignRevisionJudgementEditComponent implements OnChanges, OnDestroy {
    @Input() form?: TreatmentDesignRevisionJudgementForm | null;
    @Input() treatmentTypeId?: TreatmentTypeOriginalId | null;

    /** What the radio group offers: the liabilities still in use, plus whatever this judgement already
     * carries, so a judgement made under a since-retired liability still shows its selection. */
    protected liabilityOptions: RevisionLiability[] = [];

    protected revisionReasons?: TreatmentDesignRevisionReason[];
    protected reasonsMap = new Map<TreatmentDesignRevisionLiability, TreatmentDesignRevisionReason[]>();

    private readonly _designRevisionClient = inject(TreatmentDesignRevisionClient);
    private readonly _liabilityStore = inject(RevisionLiabilityStore);
    private _liabilityValueChangeSubscription?: Subscription;
    private _allLiabilities: RevisionLiability[] = [];

    ngOnChanges(changes: Record<keyof DesignRevisionJudgementEditComponent, SimpleChange>) {
        if (changes.form && this.form) {
            this.loadLiabilities().catch(console.warn);
        }

        if ((changes.treatmentTypeId || changes.form) && this.form && this.treatmentTypeId && !this.revisionReasons) {
            this.loadRevisionReasons().catch(console.warn);
        }

        if (changes.form && this.form) {
            this._liabilityValueChangeSubscription?.unsubscribe();
            this._liabilityValueChangeSubscription = this.form.controls.liability.valueChanges
                .pipe(distinctUntilChanged())
                .subscribe(() => {
                    this.applyLiabilityOptions();
                    this.loadRevisionReasons().catch(console.warn);
                    this.form!.controls.reasonId.patchValue(null);
                });
        }
    }

    ngOnDestroy() {
        this._liabilityValueChangeSubscription?.unsubscribe();
    }

    async loadRevisionReasons() {
        if (this.treatmentTypeId && this.form) {
            const { treatmentTypeId, form } = this;
            const liability = form?.controls.liability.value;
            if (liability != null) {
                let reasons = this.reasonsMap.get(liability);
                if (!reasons) {
                    await Promise.all(
                        this.liabilityOptions
                            .map(o => o.id)
                            .map(l =>
                                firstValueFrom(this._designRevisionClient.listRevisionReasons(treatmentTypeId, l)).then(
                                    reasons => this.reasonsMap.set(l, reasons)
                                )
                            )
                    );
                }
                reasons = this.reasonsMap.get(liability) ?? [];
                this.revisionReasons = [...reasons];
            }
        }
    }

    private async loadLiabilities(): Promise<void> {
        if (this._allLiabilities.length === 0) {
            this._allLiabilities = await firstValueFrom(this._liabilityStore.value$);
        }
        this.applyLiabilityOptions();
    }

    /**
     * A retired liability must not be offered as a new choice, but dropping it outright would blank the
     * selection on a judgement that already uses it, so the current value is always kept in the list.
     */
    private applyLiabilityOptions(): void {
        const current = this.form?.controls.liability.value;
        this.liabilityOptions = this._allLiabilities.filter(x => x.isActive || x.id === current);
    }

    protected readonly TreatmentDesignRevisionLiability = TreatmentDesignRevisionLiability;
}
